import _ from 'lodash';
import { Button, Form, Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import keywordsApi, {
  useCreateLandingUrlsMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { Formik } from 'formik';
import { object, string } from 'yup';
import { errorMessage, successMessage } from '@/utils/toast';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { addTransaction } from '@/app/common/components/ag-grid/addTransaction';
import { hideModal } from '@/app/common/reducers/simpleModal';
import useDomain from '@/hooks/useDomain';
import KeywordsForSelect from './KeywordsForSelect';
import classNames from 'classnames';
import type { GridRef } from './types';
import type { OptionProps } from 'react-select';
import type { SelectOption } from './types';

interface AddLandingUrlsDrawerProps {
  gridRef: GridRef;
}

const AddLandingUrlsDrawer = ({ gridRef }: AddLandingUrlsDrawerProps) => {
  const modalKey = useSelector(state => state.simpleModal);
  const { domain_slug } = useDomain();
  const dispatch = useDispatch();
  const [createLandingUrls] = useCreateLandingUrlsMutation();

  return (
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={modalKey === 'ADD_LANDING'}
      onHide={() => {
        dispatch(hideModal());
      }}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">افزودن صفحه هدف </div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="pb-5 pt-0">
        <Formik
          onSubmit={async ({ url, keyword_ids }, action) => {
            createLandingUrls({
              url: url.trim(),
              keyword_ids: keyword_ids.map(item => item.value.trim()),
              domain_slug
            })
              .unwrap()
              .then(res => {
                if (res?.success) {
                  successMessage('صفحه هدف با موفقیت اضافه شد');
                  addTransaction(res?.data, gridRef);
                  dispatch(keywordsApi.util.invalidateTags(['keywordsPage']));
                } else if (res?.resultCode === 50004)
                  errorMessage('صفحه هدف با این نشانی وجود داره');
              })
              .catch(err => {
                errorMessage('خطایی در ایجاد صفحه هدف وجود داره');
                console.error(err);
              });
            dispatch(hideModal());
          }}
          initialValues={{ url: '', keyword_ids: [] }}
          enableReinitialize
          validationSchema={object().shape({
            // keyword_ids: array().required('وارد کردن'),
            url: string()
              .trim()
              .required('لطفا نشانی صفحه هدف رو بنویسید.')
              .url('نشانی باید معتبر باشد.')
              .max(800, 'نشانی نباید بیشتر از ۲۰۰ کاراکتر باشد.')
          })}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) => (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <Form.Group className="mb-1">
                <Form.Label>عنوان صفحه هدف</Form.Label>
                <Form.Control
                  type="text"
                  as="textarea"
                  rows={3}
                  isInvalid={touched.url && errors.url}
                  isValid={touched.url && !errors.url}
                  style={{ direction: 'ltr' }}
                  placeholder="نشانی صفحه هدف مدنظر رو وارد کنید"
                  value={values.url}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  onBlur={handleBlur}
                  name="url"
                />
                <Form.Control.Feedback
                  className="d-block"
                  type={errors.url ? 'invalid' : 'valid'}
                >
                  {touched.url && errors.url}
                </Form.Control.Feedback>
              </Form.Group>
              <KeywordsForSelect
                label="انتخاب کلمات کلیدی صفحه هدف"
                placeholder="انتخاب کلمات کلیدی برای این صفحه هدف"
                setFieldValue={setFieldValue}
                values={values}
                footer={<></>}
              />
              {/* دکمه های آخر صفحه */}
              <div className="position-sm-absolute bottom-0 d-flex gap-2 end-0 justify-content-end p-sm-4 pb-7 pt-2">
                <Button
                  className="d-flex gap-2 align-items-center"
                  variant="outline-secondary"
                  onClick={() => dispatch(hideModal())}
                >
                  <FontAwesomeIcon size="lg" icon="times" />
                  لغو
                </Button>
                <Button
                  className="d-flex gap-2 align-items-center border-0"
                  // onClick={() => handleSubmit()}
                  type="submit"
                  variant="falcon-primary"
                >
                  <FontAwesomeIcon size="lg" icon="plus" />
                  اعمال کن
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Offcanvas.Body>

      <style>
        {/* استایل کلی دراور */}
        {`
 .offcanvas{
     width:500px !important;
      max-width:100% !important;
    }
  .offcanvas img {
    margin: 0 auto;
    max-width: 100%;
    height: auto;
    display: flex;
    }
 `}
      </style>
    </Offcanvas>
  );
};

export default AddLandingUrlsDrawer;

import type { OptionProps } from 'react-select';
import type { SelectOption } from './types';

const OptionLayout = (props: OptionProps<SelectOption>) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
          .customized-searchbox-option:hover{
            background-color:var(--falcon-gray-link-hover-300) !important;
          }
          .customized-searchbox-option{
            background-color:var(--falcon-tertiary-bg) !important;
          }
          `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className={classNames(
          'p-2 cursor-pointer border border-1 customized-searchbox-option',
          {}
        )}
      >
        <div className="fs-0 text-900">
          {props.data.label}
          {props.data.device_type === 1 ? (
            <FaDesktop className="mx-1" title="desktop" />
          ) : (
            <FaMobileScreenButton className="mx-1" title="mobile" />
          )}{' '}
          <span className="border border-400 border-1 fs--2 rounded-2 py-0 px-1">
            {props?.data?.location}
          </span>
        </div>
        <div className="text-secondary fs--2"></div>
      </div>
    </>
  );
};
